import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import { type DocumentNode, print } from "graphql"

export type GraphqlVariables = Record<string, unknown>

/** A single error from a GraphQL response. */
export type GraphqlError = {
  message: string
  path?: ReadonlyArray<string | number>
  extensions?: Record<string, unknown>
}

/** Raw GraphQL response containing both `data` and `errors` fields. */
export type GraphqlRawResult<TData> = {
  data: TData | undefined
  errors: GraphqlError[] | undefined
}

type GraphqlDocument = string | DocumentNode

/**
 * Low-level transport interface for sending GraphQL queries.
 *
 * Implement this to use a custom HTTP client, proxy, or mock.
 * Pass to {@link createGithubClient} or {@link createGraphqlClient}.
 */
export interface GraphqlTransport {
  execute<TData>(query: string, variables?: GraphqlVariables): Promise<TData>
  executeRaw?<TData>(query: string, variables?: GraphqlVariables): Promise<GraphqlRawResult<TData>>
}

/**
 * Higher-level GraphQL client with `query` and `queryRaw` methods.
 *
 * Created by {@link createGraphqlClient} from a {@link GraphqlTransport}.
 */
export interface GraphqlClient {
  query<TData, TVariables extends GraphqlVariables = GraphqlVariables>(
    query: GraphqlDocument,
    variables?: TVariables,
  ): Promise<TData>
  queryRaw<TData, TVariables extends GraphqlVariables = GraphqlVariables>(
    query: GraphqlDocument,
    variables?: TVariables,
  ): Promise<GraphqlRawResult<TData>>
}

/** Options for creating a token-based GraphQL transport. */
export type TokenClientOptions = {
  token: string
  graphqlUrl?: string
}

function queryToString(query: GraphqlDocument): string {
  if (typeof query === "string") {
    return query
  }

  if (typeof query === "object" && query !== null && "kind" in query) {
    return print(query as DocumentNode)
  }

  return String(query)
}

function assertQuery(query: string): void {
  if (query.trim().length === 0) {
    throw new Error("GraphQL query must be non-empty")
  }
}

/**
 * Create a {@link GraphqlClient} from a {@link GraphqlTransport}.
 *
 * Wraps the raw transport `execute` method with query string normalization
 * and a `queryRaw` method that returns settled results.
 */
export function createGraphqlClient(transport: GraphqlTransport): GraphqlClient {
  return {
    async query<TData, TVariables extends GraphqlVariables = GraphqlVariables>(
      query: GraphqlDocument,
      variables?: TVariables,
    ): Promise<TData> {
      const queryText = queryToString(query)
      assertQuery(queryText)
      return transport.execute<TData>(queryText, variables)
    },
    async queryRaw<TData, TVariables extends GraphqlVariables = GraphqlVariables>(
      query: GraphqlDocument,
      variables?: TVariables,
    ): Promise<GraphqlRawResult<TData>> {
      const queryText = queryToString(query)
      assertQuery(queryText)
      // Both paths normalize transport-level errors into settled results
      try {
        if (transport.executeRaw) {
          return await transport.executeRaw<TData>(queryText, variables)
        }
        const data = await transport.execute<TData>(queryText, variables)
        return { data, errors: undefined }
      } catch (err) {
        return {
          data: undefined,
          errors: [{ message: err instanceof Error ? err.message : String(err) }],
        }
      }
    },
  }
}

/** Execute a generated typed document through the public transport contract. */
export function executeTypedDocument<TData, TVariables extends GraphqlVariables = GraphqlVariables>(
  transport: GraphqlTransport,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
): Promise<TData> {
  const queryText = queryToString(document)
  assertQuery(queryText)
  return transport.execute<TData>(queryText, variables)
}

const DEFAULT_GRAPHQL_URL = "https://api.github.com/graphql"

export function resolveGraphqlUrl(): string {
  if (process.env.GITHUB_GRAPHQL_URL) {
    return process.env.GITHUB_GRAPHQL_URL
  }

  if (process.env.GH_HOST) {
    return `https://${process.env.GH_HOST}/api/graphql`
  }

  return DEFAULT_GRAPHQL_URL
}

type JsonPayload<TData> = {
  data?: TData
  errors?: GraphqlError[]
  message?: string
}

async function fetchGraphql<TData>(
  url: string,
  token: string,
  query: string,
  variables?: GraphqlVariables,
): Promise<JsonPayload<TData>> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables: variables ?? {} }),
  })

  if (!response.ok) {
    let message = `GraphQL request failed (${response.status})`
    try {
      const body = (await response.json()) as JsonPayload<TData>
      if (body.message) {
        message = body.message
      }
    } catch {
      // Non-JSON error body — use status-based message
    }
    throw new Error(message)
  }

  try {
    return (await response.json()) as JsonPayload<TData>
  } catch {
    throw new Error(`GraphQL response is not valid JSON (${response.status})`)
  }
}

export function createTokenTransport(token: string, graphqlUrl?: string): GraphqlTransport {
  const url = graphqlUrl ?? resolveGraphqlUrl()

  return {
    async execute<TData>(query: string, variables?: GraphqlVariables): Promise<TData> {
      const payload = await fetchGraphql<TData>(url, token, query, variables)

      if (payload.errors?.length) {
        throw new Error(payload.errors[0]?.message ?? "GraphQL returned errors")
      }

      if (payload.data === undefined) {
        throw new Error("GraphQL response missing data")
      }

      return payload.data
    },

    async executeRaw<TData>(
      query: string,
      variables?: GraphqlVariables,
    ): Promise<GraphqlRawResult<TData>> {
      const payload = await fetchGraphql<TData>(url, token, query, variables)
      return {
        data: payload.data,
        errors: payload.errors?.length ? payload.errors : undefined,
      }
    },
  }
}
